// about-modal.ts
// "About me" floating pill → expanding modal, anchored bottom-left of hero.
// Architecture: single container, single icon. bottomRow is always visible at
// the container's bottom edge; it holds the icon, plus either the pill label
// (pill state) or the chat input (modal state). modalContent sits above it.

import { FONT, COLOR, RADIUS, TRANSITION, TEXT_STYLE, applyTextStyle } from "./tokens";

// ── Layout constants ──────────────────────────────────────────────────────────
const PILL_H          = 52;
const PILL_W          = 168;
const BOTTOM_ROW_H    = 64;
const MODAL_W_MAX     = 380;
const MODAL_H         = 700;
const BOTTOM_OFFSET   = 32;
const PILL_HOVER_SCALE = 1.09;

// ── Sphere rotation speeds (must match main.ts tick)
// yaw  t*0.34 rad/s → 2π/0.34 ≈ 18.5s per revolution  (outer ring CW)
// pitch t*0.22 rad/s → 2π/0.22 ≈ 28.6s per revolution (inner ring CCW)
const INNER_SPIN_DURATION = "28.6s";
const OUTER_SPIN_DURATION = "18.5s";

// ── Asset base (Netlify host) — root-relative paths break when the bundle
//    runs embedded on a different domain (e.g. Webflow). All assets that live
//    on the Netlify deploy must use absolute URLs.
const ASSET_BASE = "https://vocal-nougat-e2d132.netlify.app";

// ── Scroll prevention — stored so the same listener reference can be removed
let _preventScroll: ((e: Event) => void) | null = null;
let _onTouchStart: ((e: TouchEvent) => void) | null = null;
let _lastTouchY = 0;

// ── Copy constants ────────────────────────────────────────────────────────────
const PHOTO_SRC = ASSET_BASE + "/images/Justis-Frame.png";
const VIDEO_SRC = ASSET_BASE + "/video/portrait-loop-v0.mp4";

const BIO_TEXT =
  "As a product designer, I craft innovative digital experiences for global brands, " +
  "blending strategy, creativity and curiosity to create the experiences of tomorrow. ";

const CHIPS: { label: string; response: string }[] = [
  {
    label: "How do you work?",
    response:
      "It always starts with context, I want to become something close to an expert " +
      "in the space before I touch the problem. That means understanding the domain, " +
      "the client, the goal, why we're here. I take a lot of notes, on paper usually — " +
      "I find writing it down reinforces the message. Then I get tactical. That might look like " +
      "sketching concepts in Figma, discussing user flows with a colleague, or prototyping an interaction " +
      "with Claude Code. Lately though, i've been experimenting a lot with how I work. So we'll see where that takes me."
  },
  {
    label: "AI and design",
    response:
      "I see AI as a tool, and for designers, a tool that lowers the barrier between idea " +
      "and execution. It makes it easier to turn something abstract into something tangible " +
      "— something people can use, test, and interact with.\n\n" +
      "That changes what feels possible. If technical know-how was once a blocker to " +
      "creative expression, what happens when that blocker starts to fall away?\n\n" +
      "I think that shifts the value of design too. Less of it sits in execution alone, and " +
      "more of it sits in discretion, vision, strategy, and taste — knowing what's worth " +
      "making, why it matters, and how it should feel.\n\n" +
      "Still, we need to be intentional. Designers have to decide which skills they want to " +
      "keep sharpening, and which ones they're comfortable letting tools absorb. There isn't " +
      "one right answer to that, but I think it's an important question to keep asking.",
  },
  {
    label: "What makes work good?",
    response:
      "Work is good when it clearly understands its audience and then does something " +
      "with that understanding. On one hand it says: I know exactly what you're here for, " +
      "and I'm built around that. On the other, it finds moments to surprise, delight and engage you. Things " +
      "you didn't necessarily ask for, but they demonstrate a real read of who you are. That balance," +
      " precision and a little unexpected, is what I'm usually chasing.",
  },
];

const CHAT_API_URL = "https://justis-chat-api.justis-croasdale94.workers.dev/chat";

function track(event: string, props?: Record<string, unknown>) {
  try { (window as any).posthog?.capture?.(event, props); } catch {}
}

async function fetchReply(message: string): Promise<string> {
  try {
    const res = await fetch(CHAT_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages: [{ role: "user", content: message }] }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json() as { reply?: string };
    return data.reply ?? "Sorry, something went wrong. Please try again.";
  } catch {
    return "Sorry, I couldn't reach the server. Please try again.";
  }
}

// ── Dot icon constants ────────────────────────────────────────────────────────
const DOT_ICON_INNER_RADIUS_FRAC = 0.19;
const DOT_ICON_OUTER_RADIUS_FRAC = 0.40;
const DOT_ICON_DOT_RADIUS_FRAC   = 0.045;
const DOT_ICON_INNER_COUNT       = 6;
const DOT_ICON_OUTER_COUNT       = 11;

// ── Skills data ───────────────────────────────────────────────────────────────
const SKILLS_DATA = [
  { label: "AI",               maxFill: 0.82 },
  { label: "IA",               maxFill: 0.85 },
  { label: "INT. DESIGN",      maxFill: 0.90 },
  { label: "PROD. STRATEGY",   maxFill: 0.76 },
  { label: "SYSTEMS THINKING", maxFill: 0.88 },
  { label: "DESIGN SYSTEMS",   maxFill: 0.84 },
  { label: "USER RESEARCH",    maxFill: 0.78 },
  { label: "FACILITATION",     maxFill: 0.71 },
];

type TabId = "overview" | "skills" | "clients";
const TAB_DEFS: { id: TabId; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "skills",   label: "Skills"   },
  { id: "clients",  label: "Clients"  },
];

const EASE = "cubic-bezier(0.4,0,0.2,1)";
const RETRIEVE_DURATION_MS = 2700;

// ── Text animation mode ────────────────────────────────────────────────────
// Evaluate via ?textAnim=word-drift|word-fade|char-drift|block-slide
// Default: word-drift. DEV replay button injected at bottom-right.
type TextAnimMode = "word-fade" | "word-drift" | "char-drift" | "block-slide";
function readTextAnimMode(): TextAnimMode {
  const p = new URLSearchParams(location.search).get("textAnim");
  if (p === "word-fade" || p === "char-drift" || p === "block-slide") return p;
  return "word-drift";
}
const TEXT_ANIM_MODE = readTextAnimMode();

// ── Helpers ───────────────────────────────────────────────────────────────────
function localLeft(): number {
  const anchor = document.getElementById("left-edge");
  if (anchor) return anchor.getBoundingClientRect().left;
  return Math.max(window.innerWidth * 0.05, (window.innerWidth - 1200) / 2);
}
function localRight(): number {
  const anchor = document.getElementById("nav-container");
  if (anchor) return anchor.getBoundingClientRect().right;
  return window.innerWidth - Math.max(window.innerWidth * 0.05, (window.innerWidth - 1200) / 2);
}
function modalWidth(): number {
  if (window.innerWidth <= 768) return localRight() - localLeft();
  return Math.min(MODAL_W_MAX, window.innerWidth - 32);
}
function modalHeight(): number { return Math.min(MODAL_H, window.innerHeight - BOTTOM_OFFSET - 16); }
/** Left position for the pill — centred on mobile, left-anchored on desktop. */
function pillLeft(): number {
  if (window.innerWidth <= 768) return Math.round((window.innerWidth - PILL_W) / 2);
  return localLeft();
}
function wait(ms: number): Promise<void> { return new Promise(r => setTimeout(r, ms)); }

interface DotIconController {
  el: HTMLElement;
  startRetrieve(direction?: "cw" | "ccw"): void;
  stopRetrieve(): void;
  setRadius(scale: number): void;
  pulseRadius(peakScale: number, durationMs: number): void;
  squeezeRadius(dipScale: number, durationMs: number): void;
  startOuterWalk(direction: "cw" | "ccw"): void;
  stopOuterWalk(): void;
  rotateInner(deg: number, durationMs: number): void;
  rotateOuter(deg: number, durationMs: number): void;
  resetRotation(): void;
}

interface InfoDot {
  el:   HTMLElement;
  show: () => void;
  hide: () => void;
}

interface ResponseCtx {
  defaultEl:      HTMLElement;
  defaultDisplay: string;    // "flex" for overview, "block" for skills/clients
  responseEl:     HTMLElement;
  youMsgEl:       HTMLElement;
  justisMsgEl:    HTMLElement;
  infoDot:        InfoDot;
  onHide:         () => void;
  _savedHeight?:  number;
}

function makeDotIcon(size: number): DotIconController {
  const wrap = document.createElement("div");
  wrap.style.cssText = `position:relative;width:${size}px;height:${size}px;flex-shrink:0;pointer-events:none;`;

  const cx = size / 2, cy = size / 2;
  const dotR = size * DOT_ICON_DOT_RADIUS_FRAC;

  const ringDefs: [number, number, string][] = [
    [size * DOT_ICON_INNER_RADIUS_FRAC, DOT_ICON_INNER_COUNT, `amSpinCCW ${INNER_SPIN_DURATION} linear infinite`],
    [size * DOT_ICON_OUTER_RADIUS_FRAC, DOT_ICON_OUTER_COUNT, `amSpinCW  ${OUTER_SPIN_DURATION} linear infinite`],
  ];

  let outerDotEls: HTMLElement[] = [];
  const ringWrappers: HTMLElement[] = [];

  ringDefs.forEach(([r, n, anim], ringIdx) => {
    const ringWrapper = document.createElement("div");
    ringWrapper.style.cssText = "position:absolute;inset:0;transform-origin:center;";
    const ring = document.createElement("div");
    ring.style.cssText = [
      "position:absolute;inset:0",
      `transform-origin:${cx}px ${cy}px`,
      `animation:${anim}`,
    ].join(";");
    const dots: HTMLElement[] = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      const dot = document.createElement("span");
      dot.style.cssText = [
        "position:absolute",
        `width:${(dotR*2).toFixed(1)}px;height:${(dotR*2).toFixed(1)}px`,
        "border-radius:50%;background:#fff",
        `left:${(cx + r*Math.cos(a) - dotR).toFixed(1)}px`,
        `top:${(cy + r*Math.sin(a) - dotR).toFixed(1)}px`,
      ].join(";");
      dots.push(dot);
      ring.appendChild(dot);
    }
    ringWrapper.appendChild(ring);
    wrap.appendChild(ringWrapper);
    ringWrappers.push(ringWrapper);
    if (ringIdx !== 0) outerDotEls = dots;
  });

  // ── Rotation state (accumulated, so repeat calls stack correctly) ─────────
  let innerRotDeg = 0;
  let outerRotDeg = 0;

  function rotateInner(deg: number, durationMs: number) {
    innerRotDeg += deg;
    ringWrappers[0]!.style.transition = `transform ${durationMs}ms ease`;
    ringWrappers[0]!.style.transform  = `scale(1) rotate(${innerRotDeg}deg)`;
  }
  function rotateOuter(deg: number, durationMs: number) {
    outerRotDeg += deg;
    ringWrappers[1]!.style.transition = `transform ${durationMs}ms ease`;
    ringWrappers[1]!.style.transform  = `scale(1) rotate(${outerRotDeg}deg)`;
  }
  function resetRotation() {
    innerRotDeg = 0;
    outerRotDeg = 0;
    ringWrappers[0]!.style.transition = "none";
    ringWrappers[1]!.style.transition = "none";
    ringWrappers[0]!.style.transform  = "scale(1) rotate(0deg)";
    ringWrappers[1]!.style.transform  = "scale(1) rotate(0deg)";
  }

  // ── Radius pulse ───────────────────────────────────────────────────────────
  let radiusRaf: number | null = null;

  function setRadius(scale: number) {
    ringWrappers[0]!.style.transform = `scale(${scale}) rotate(${innerRotDeg}deg)`;
    ringWrappers[1]!.style.transform = `scale(${scale}) rotate(${outerRotDeg}deg)`;
  }

  function radiusAnim(getScale: (t: number) => number, durationMs: number) {
    if (radiusRaf !== null) { cancelAnimationFrame(radiusRaf); radiusRaf = null; }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / durationMs, 1);
      setRadius(getScale(t));
      if (t < 1) { radiusRaf = requestAnimationFrame(tick); } else { radiusRaf = null; setRadius(1); }
    };
    radiusRaf = requestAnimationFrame(tick);
  }

  function pulseRadius(peakScale: number, durationMs: number) {
    const peak2 = 1 + (peakScale - 1) * 0.65;
    radiusAnim(t => {
      if (t < 0.35) return 1 + (peakScale - 1) * (t / 0.35);
      if (t < 0.55) return peakScale + (1 - peakScale) * ((t - 0.35) / 0.20);
      if (t < 0.80) return 1 + (peak2 - 1) * ((t - 0.55) / 0.25);
      return peak2 + (1 - peak2) * ((t - 0.80) / 0.20);
    }, durationMs);
  }

  function squeezeRadius(dipScale: number, durationMs: number) {
    const dip2 = 1 - (1 - dipScale) * 0.65;
    radiusAnim(t => {
      if (t < 0.35) return 1 + (dipScale - 1) * (t / 0.35);
      if (t < 0.55) return dipScale + (1 - dipScale) * ((t - 0.35) / 0.20);
      if (t < 0.80) return 1 + (dip2 - 1) * ((t - 0.55) / 0.25);
      return dip2 + (1 - dip2) * ((t - 0.80) / 0.20);
    }, durationMs);
  }

  // ── Inner ring retrieve — 3 pulsed CW rotations ──────────────────────────
  let retrieveAnim: Animation | null = null;

  function startRetrieve(_direction?: "cw" | "ccw") {
    if (retrieveAnim) { retrieveAnim.cancel(); retrieveAnim = null; }
    const startDeg = innerRotDeg;
    // Each iteration is one full CW rotation with ease-in-out — accelerates then
    // decelerates to a near-stop, giving three clearly separate "pulse" spins.
    // 3 × 320ms = 960ms fits within RETRIEVE_DURATION_MS.
    retrieveAnim = ringWrappers[0]!.animate(
      [
        { transform: `scale(1) rotate(${startDeg}deg)` },
        { transform: `scale(1) rotate(${startDeg + 360}deg)` },
      ],
      { duration: 900, iterations: Infinity, easing: "ease-in-out", fill: "forwards" }
    );
  }

  function stopRetrieve() {
    if (!retrieveAnim) return;
    const el = ringWrappers[0]!;
    const m = new DOMMatrix(getComputedStyle(el).transform);
    const currentAngle = Math.atan2(m.b, m.a) * (180 / Math.PI);
    el.style.transition = "none";
    el.style.transform  = `scale(1) rotate(${currentAngle}deg)`;
    retrieveAnim.cancel();
    retrieveAnim = null;
    innerRotDeg = currentAngle;
  }

  // ── Outer ring walk (tab navigation) ──────────────────────────────────────
  let outerWalkTimer: ReturnType<typeof setInterval> | null = null;
  let outerPos = 0;

  function startOuterWalk(direction: "cw" | "ccw") {
    stopOuterWalk();
    outerPos = 0;
    const step = () => {
      outerDotEls.forEach((d, i) => {
        d.style.transition = "opacity 150ms ease";
        d.style.opacity = i === outerPos ? "0.15" : "1";
      });
      outerPos = direction === "cw"
        ? (outerPos + 1) % outerDotEls.length
        : (outerPos - 1 + outerDotEls.length) % outerDotEls.length;
    };
    step();
    outerWalkTimer = setInterval(step, 55);
  }

  function stopOuterWalk() {
    if (outerWalkTimer) { clearInterval(outerWalkTimer); outerWalkTimer = null; }
    outerDotEls.forEach(d => { d.style.transition = "opacity 250ms ease"; d.style.opacity = "1"; });
  }

  return { el: wrap, startRetrieve, stopRetrieve, setRadius, pulseRadius, squeezeRadius, startOuterWalk, stopOuterWalk, rotateInner, rotateOuter, resetRotation };
}

function makeEyebrow(text: string): HTMLElement {
  const el = document.createElement("div");
  el.textContent = text;
  el.style.cssText = `color:${COLOR.textDim};margin-bottom:4px`;
  applyTextStyle(el, "eyebrow");
  return el;
}

function makeCloseBtn(label: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.className = "am-close-btn";
  btn.innerHTML = label;
  btn.style.cssText = [
    "width:28px;height:28px;border-radius:50%",
    `border:1px solid ${COLOR.border}`,
    `background:${COLOR.msgBg};color:#fff`,
    "font-size:11px;cursor:pointer;flex-shrink:0",
    "display:flex;align-items:center;justify-content:center",
    `font-family:${FONT.family}`,
  ].join(";");
  return btn;
}

function makeInfoDot(): InfoDot {
  const isMobile = !window.matchMedia("(pointer: fine)").matches;

  const wrap = document.createElement("div");
  wrap.style.cssText = "position:relative;display:inline-flex;margin-top:16px;opacity:0;transition:opacity 300ms ease;";

  const dot = document.createElement("button");
  dot.style.cssText = [
    "width:28px;height:28px;border-radius:50%",
    `border:1px solid rgba(255,255,255,0.35)`,
    "background:none;cursor:pointer;padding:0;flex-shrink:0",
    "display:flex;align-items:center;justify-content:center",
    `color:${COLOR.textFull};font-family:${FONT.family}`,
    "font-size:12px;font-weight:300",
    "transition:border-color 150ms ease,color 150ms ease,background 150ms ease",
  ].join(";");
  dot.textContent = "i";

  const tooltip = document.createElement("div");
  tooltip.style.cssText = [
    "position:absolute;bottom:calc(100% + 8px);left:0",
    "width:240px",
    `background:${COLOR.surface}`,
    `border:1px solid ${COLOR.border}`,
    `border-radius:${RADIUS.card}`,
    "padding:10px 12px",
    "opacity:0;pointer-events:none",
    "transition:opacity 150ms ease",
    "z-index:10",
  ].join(";");

  const DISCLAIMER = "This response was AI generated. Although it's based on my background, there may be some inaccuracies. If you're interested in the most accurate and up to date information, reach out on LinkedIn.";
  const tooltipText = document.createElement("p");
  tooltipText.textContent = DISCLAIMER;
  tooltipText.style.cssText = `color:${COLOR.text};margin:0;`;
  applyTextStyle(tooltipText, "bodySmall");
  tooltip.appendChild(tooltipText);

  const showTooltip = () => {
    tooltip.style.opacity = "1";
    tooltip.style.pointerEvents = "auto";
  };
  const hideTooltip = () => {
    tooltip.style.opacity = "0";
    tooltip.style.pointerEvents = "none";
  };

  if (isMobile) {
    const closeBtn = document.createElement("button");
    closeBtn.innerHTML = "&#x2715;";
    closeBtn.style.cssText = [
      "position:absolute;top:-14px;right:-14px",
      "width:28px;height:28px;border-radius:50%",
      `border:1px solid ${COLOR.border}`,
      `background:${COLOR.msgBg};color:#fff`,
      "font-size:11px;cursor:pointer;flex-shrink:0;padding:0",
      "display:flex;align-items:center;justify-content:center",
      `font-family:${FONT.family}`,
    ].join(";");
    tooltip.appendChild(closeBtn);

    dot.addEventListener("click", (e) => {
      e.stopPropagation();
      if (tooltip.style.pointerEvents !== "none") { hideTooltip(); return; }
      showTooltip();
      const outsideClick = (ev: MouseEvent) => {
        if (!wrap.contains(ev.target as Node)) {
          hideTooltip();
          document.removeEventListener("click", outsideClick, true);
        }
      };
      setTimeout(() => document.addEventListener("click", outsideClick, true), 0);
    });
    closeBtn.addEventListener("click", (e) => { e.stopPropagation(); hideTooltip(); });
  } else {
    dot.addEventListener("mouseenter", () => {
      showTooltip();
      dot.style.background = COLOR.textFull;
      dot.style.borderColor = COLOR.textFull;
      dot.style.color = COLOR.surface;
    });
    dot.addEventListener("mouseleave", () => {
      hideTooltip();
      dot.style.background = "none";
      dot.style.borderColor = "rgba(255,255,255,0.35)";
      dot.style.color = COLOR.textFull;
    });
  }

  wrap.appendChild(dot);
  wrap.appendChild(tooltip);

  return {
    el:   wrap,
    show: () => requestAnimationFrame(() => requestAnimationFrame(() => { wrap.style.opacity = "1"; })),
    hide: () => { wrap.style.opacity = "0"; hideTooltip(); },
  };
}

// ── Main export ───────────────────────────────────────────────────────────────
export function initAboutModal(opts?: {
  onItemHover?: (label: string | null) => void;
  onOpen?: () => void;
  onClose?: () => void;
}): { isOpen: () => boolean } {

  // ── Injected styles ──────────────────────────────────────────────────────
  const style = document.createElement("style");
  style.textContent = `
    @keyframes amSpinCCW { to { transform: rotate(-360deg); } }
    @keyframes amSpinCW  { to { transform: rotate( 360deg); } }
    .am-body::-webkit-scrollbar { display: none; }
    .am-chip:hover { background: ${COLOR.chipBgHover} !important; }
    .am-close-btn:hover { background: ${COLOR.chipBgHover} !important; }
    .am-input::placeholder { color: ${COLOR.inputText}; }
    .am-input:focus { outline: none; }
    .am-skill-row { margin-bottom: 28px; }
    .am-skill-label {
      font-family: ${FONT.family};
      font-size: ${TEXT_STYLE.tab.size};
      font-weight: ${TEXT_STYLE.tab.weight};
      letter-spacing: ${TEXT_STYLE.tab.tracking};
      text-transform: uppercase;
      color: ${COLOR.textFull};
      margin-bottom: 10px;
    }
    .am-skill-row .skill-meter {
      display: grid;
      width: 100%;
      grid-template-columns: repeat(32, 1fr);
      grid-template-rows: repeat(3, 6px);
      gap: 3px;
    }
    .am-skill-row .sg-dot {
      width: 2.5px; height: 2.5px;
      border-radius: 50%;
      background: #fff;
      align-self: center; justify-self: center;
      transition: opacity 400ms ease;
    }
  `;
  document.head.appendChild(style);

  // ── Container ──────────────────────────────────────────────────────────────
  const container = document.createElement("div");
  container.id = "about-modal";
  container.style.cssText = [
    "position:fixed",
    `bottom:${BOTTOM_OFFSET}px`,
    `left:${pillLeft()}px`,
    `width:${PILL_W}px`,
    `height:${PILL_H}px`,
    `background:${COLOR.surface}`,
    `border-radius:${PILL_H / 2}px`,
    "overflow:hidden",
    "cursor:pointer",
    "z-index:9985",
    "box-sizing:border-box",
    "will-change:width,height,border-radius,transform",
    "transition:transform 300ms ease",
    "user-select:none",
  ].join(";");
  document.body.appendChild(container);

  // ── Modal content (sits above bottomRow, hidden in pill state) ─────────────
  const modalContent = document.createElement("div");
  modalContent.style.cssText = [
    "position:absolute;top:0;left:0;right:0",
    `bottom:${BOTTOM_ROW_H}px`,
    "display:flex;flex-direction:column",
    "opacity:0;pointer-events:none;overflow:hidden",
  ].join(";");
  container.appendChild(modalContent);

  // Header ───────────────────────────────────────────────────────────────────
  const header = document.createElement("div");
  header.style.cssText = "display:flex;align-items:center;justify-content:space-between;padding:16px 16px 12px;flex-shrink:0;";

  const tabsWrap = document.createElement("div");
  tabsWrap.style.cssText = "display:flex;gap:20px;";
  const tabEls = new Map<TabId, HTMLButtonElement>();
  let activeTab: TabId = "overview";

  function tabColor(id: TabId) { return id === activeTab ? COLOR.textFull : COLOR.textDim; }
  function tabWeight(id: TabId) { return id === activeTab ? "500" : TEXT_STYLE.tab.weight; }

  TAB_DEFS.forEach(({ id, label }) => {
    const btn = document.createElement("button");
    btn.textContent = label.toUpperCase();
    btn.style.cssText = [
      "background:none;border:none;padding:0;cursor:pointer",
      `color:${tabColor(id)}`,
      `transition:${TRANSITION.color},text-shadow 900ms ease`,
    ].join(";");
    applyTextStyle(btn, "tab");
    btn.style.fontWeight = tabWeight(id);
    btn.addEventListener("mouseenter", () => {
      btn.style.color = COLOR.textFull;
    });
    btn.addEventListener("mouseleave", () => {
      btn.style.color = tabColor(id);
    });
    btn.addEventListener("click", e => {
      e.stopPropagation();
      track("tab_switched", { tab: id });
      // Flash glow then fade — same feel as scroll progress dots
      btn.style.transition = `${TRANSITION.color},text-shadow 0ms`;
      btn.style.textShadow = "0 0 8px rgba(255,255,255,0.85)";
      requestAnimationFrame(() => requestAnimationFrame(() => {
        btn.style.transition = `${TRANSITION.color},text-shadow 900ms ease`;
        btn.style.textShadow = "none";
      }));
      switchTab(id);
    });
    tabsWrap.appendChild(btn);
    tabEls.set(id, btn);
  });

  const closeBtn = makeCloseBtn("&#x2715;");
  closeBtn.addEventListener("click", e => { e.stopPropagation(); closeModal(); });

  header.appendChild(tabsWrap);
  header.appendChild(closeBtn);
  modalContent.appendChild(header);

  const divider = document.createElement("div");
  divider.style.cssText = `height:1px;background:${COLOR.borderSubtle};margin:0 16px;flex-shrink:0;`;
  modalContent.appendChild(divider);

  // Body ─────────────────────────────────────────────────────────────────────
  const body = document.createElement("div");
  body.className = "am-body";
  body.style.cssText = "flex:1;overflow-y:auto;scrollbar-width:none;";
  modalContent.appendChild(body);

  // ── Bottom row (always visible — single icon + label OR input) ─────────────
  const bottomRow = document.createElement("div");
  bottomRow.style.cssText = [
    "position:absolute;bottom:0;left:0;right:0",
    `height:${PILL_H}px`,
    "display:flex;align-items:center;gap:16px",
    "padding:0 14px",
    "box-sizing:border-box",
    "border-top:0px solid transparent",  // toggled on modal open
    "transition:transform 300ms ease",   // issue 2: smooth counter-scale
  ].join(";");

  const dotIconCtrl = makeDotIcon(30);
  bottomRow.appendChild(dotIconCtrl.el);

  // Pill label (visible in pill state only)
  const pillLabel = document.createElement("span");
  pillLabel.textContent = "About me";
  pillLabel.style.cssText = "color:#fff;white-space:nowrap;flex-shrink:0";
  applyTextStyle(pillLabel, "body");
  bottomRow.appendChild(pillLabel);

  // Chat input (hidden in pill state, shown in modal state)
  const chatInput = document.createElement("input");
  chatInput.type = "text";
  chatInput.placeholder = "Ask me anything…";
  chatInput.className = "am-input";
  chatInput.style.cssText = [
    `flex:1;background:transparent;border:none;color:${COLOR.text}`,
    "opacity:0;min-width:0;pointer-events:none",
  ].join(";");
  applyTextStyle(chatInput, "inputText");
  if (!window.matchMedia("(pointer: fine)").matches) {
    chatInput.style.fontSize = "16px"; // prevent iOS Safari auto-zoom on focus
  }
  bottomRow.appendChild(chatInput);

  // Submit arrow (hidden in pill state, shown in modal state — sibling of chatInput)
  const submitBtn = document.createElement("button");
  submitBtn.type = "button";
  submitBtn.style.cssText = [
    "flex-shrink:0;background:transparent;padding:0;margin:0",
    `border-radius:50%;border:1px solid ${COLOR.border}`,
    `color:${COLOR.textFull};cursor:pointer`,
    "opacity:0;pointer-events:none",
    "display:flex;align-items:center;justify-content:center",
    "width:30px;height:30px",
    "transition:border-color 150ms ease,color 150ms ease,background 150ms ease",
  ].join(";");
  submitBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 13V1M1.5 6.5L7 1L12.5 6.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  function setSubmitHighlighted(on: boolean) {
    submitBtn.style.background  = on ? COLOR.textFull  : "transparent";
    submitBtn.style.borderColor = on ? COLOR.textFull  : COLOR.border;
    submitBtn.style.color       = on ? COLOR.surface   : COLOR.textFull;
  }

  submitBtn.addEventListener("mouseenter", () => { if (!chatInput.disabled) setSubmitHighlighted(true); });
  submitBtn.addEventListener("mouseleave", () => { if (!chatInput.value.trim()) setSubmitHighlighted(false); });
  submitBtn.addEventListener("click", e => {
    e.stopPropagation();
    const msg = chatInput.value.trim();
    if (!msg) return;
    chatInput.value = "";
    setSubmitHighlighted(false);
    track("question_submitted", { question: msg });
    showResponse(msg, fetchReply(msg), activeCtx());
  });
  bottomRow.appendChild(submitBtn);

  container.appendChild(bottomRow);

  // Border overlay — renders above all children so inset border is never covered
  // (inset box-shadow on container is painted below content children)
  const borderOverlay = document.createElement("div");
  borderOverlay.style.cssText = [
    "position:absolute;inset:0;border-radius:inherit",
    "box-shadow:inset 0 0 0 1px #fff",
    "pointer-events:none;z-index:9999",
  ].join(";");
  container.appendChild(borderOverlay);

  // ── Response contexts (assigned after all panels are built) ────────────────
  let overviewCtx!: ResponseCtx;
  let skillsCtx!:   ResponseCtx;
  let clientsCtx!:  ResponseCtx;

  // ── Overview panel ─────────────────────────────────────────────────────────
  const overviewPanel = document.createElement("div");
  overviewPanel.style.cssText = "display:flex;flex-direction:column;height:100%;";

  // Default view
  const overviewDefault = document.createElement("div");
  overviewDefault.style.cssText = "display:flex;flex-direction:column;flex:1;min-height:0;";

  const photoWrap = document.createElement("div");
  photoWrap.id = "am-photo-wrap";
  photoWrap.style.cssText = [
    "position:relative;width:100%",
    "overflow:hidden;flex:1;min-height:0",
  ].join(";");
  const photoVideo = document.createElement("video");
  photoVideo.src = VIDEO_SRC;
  photoVideo.poster = PHOTO_SRC;
  photoVideo.muted = true;
  photoVideo.loop = true;
  photoVideo.playsInline = true;
  photoVideo.setAttribute("autoplay", "");
  photoVideo.style.cssText =
    "width:100%;height:100%;object-fit:cover;object-position:top;display:block;" +
    "transform-origin:top center;transform:scale(1.2) translateX(3.5%) translateY(-24px)";
  photoWrap.appendChild(photoVideo);

  const photoGradient = document.createElement("div");
  photoGradient.style.cssText = [
    "position:absolute;bottom:0;left:0;right:0",
    "height:100%",
    "background:linear-gradient(to top, #080808 0%, #080808 11%, rgba(8,8,8,0) 70%)",
    "pointer-events:none",
  ].join(";");
  photoWrap.appendChild(photoGradient);

  const photoCover = document.createElement("div");
  photoCover.style.cssText =
    "position:absolute;inset:0;background:#080808;pointer-events:none;transition:opacity 700ms ease";

  photoWrap.appendChild(photoCover);

  overviewDefault.appendChild(photoWrap);

  const hasDrift = TEXT_ANIM_MODE === "word-drift" || TEXT_ANIM_MODE === "char-drift";
  const bioTokens: HTMLElement[] = [];
  const bioWrap = document.createElement("div");
  bioWrap.id = "am-bio-wrap";
  bioWrap.style.cssText = "position:absolute;bottom:16px;left:0;right:0;padding:0 16px;";
  if (TEXT_ANIM_MODE === "block-slide") {
    bioWrap.textContent = BIO_TEXT;
    applyTextStyle(bioWrap, "body");
    bioWrap.style.color     = COLOR.text;
    bioWrap.style.opacity   = "0";
    bioWrap.style.transform = "translateY(8px)";
  } else if (TEXT_ANIM_MODE === "char-drift") {
    [...BIO_TEXT].forEach(char => {
      const span = document.createElement("span");
      span.textContent = char;
      span.style.cssText = `display:inline;color:${COLOR.text};opacity:0;transform:translateY(6px)`;
      applyTextStyle(span, "body");
      bioWrap.appendChild(span);
      bioTokens.push(span);
    });
  } else {
    // word-fade, word-drift
    BIO_TEXT.split(" ").forEach(word => {
      const span = document.createElement("span");
      span.textContent = word;
      span.style.cssText = `display:inline-block;color:${COLOR.text};opacity:0${hasDrift ? ";transform:translateY(6px)" : ""}`;
      applyTextStyle(span, "body");
      bioWrap.appendChild(span);
      bioWrap.appendChild(document.createTextNode(" "));
      bioTokens.push(span);
    });
  }
  photoWrap.appendChild(bioWrap);

  const chipsRow = document.createElement("div");
  chipsRow.style.cssText = "display:flex;flex-wrap:wrap;gap:11px 8px;padding:20px 16px 24px;flex-shrink:0;";
  CHIPS.forEach(chip => {
    const btn = document.createElement("button");
    btn.textContent = chip.label;
    btn.className = "am-chip";
    btn.style.cssText = [
      `background:${COLOR.chipBg}`,
      `border:1px solid ${COLOR.border}`,
      `border-radius:${RADIUS.pill}`,
      "padding:14px 14px",
      "color:rgba(255,255,255,0.75);cursor:pointer",
      "transition:background 0.15s ease",
    ].join(";");
    applyTextStyle(btn, "chip");
    btn.style.opacity = "0";
    if (hasDrift) btn.style.transform = "translateY(6px)";
    btn.addEventListener("click", e => { e.stopPropagation(); track("chip_clicked", { label: chip.label }); showResponse(chip.label, chip.response, overviewCtx); });
    chipsRow.appendChild(btn);
  });
  overviewDefault.appendChild(chipsRow);

  // Response view
  const overviewResponse = document.createElement("div");
  overviewResponse.style.cssText = "display:none;flex-direction:column;padding:12px 16px;gap:16px;opacity:0;";

  const responseHeader = document.createElement("div");
  responseHeader.style.cssText = "display:flex;align-items:center;";
  const backBtn = makeCloseBtn("&#x2190;");
  responseHeader.appendChild(backBtn);
  overviewResponse.appendChild(responseHeader);

  const youSection = document.createElement("div");
  const youMsg = document.createElement("div");
  youMsg.style.cssText = [
    "color:rgba(255,255,255,0.85)",
    `background:${COLOR.msgBg}`,
    `border-radius:${RADIUS.card}`,
    "padding:8px 12px;margin-top:4px;display:inline-block",
  ].join(";");
  applyTextStyle(youMsg, "inputText");
  youSection.appendChild(makeEyebrow("You"));
  youSection.appendChild(youMsg);
  overviewResponse.appendChild(youSection);

  const justisSection = document.createElement("div");
  const justisMsg = document.createElement("div");
  justisMsg.style.cssText = `color:${COLOR.text};margin-top:4px`;
  applyTextStyle(justisMsg, "body");
  const overviewInfoDot = makeInfoDot();
  justisSection.appendChild(makeEyebrow("Justis"));
  justisSection.appendChild(justisMsg);
  justisSection.appendChild(overviewInfoDot.el);
  overviewResponse.appendChild(justisSection);

  overviewPanel.appendChild(overviewDefault);
  overviewPanel.appendChild(overviewResponse);

  // ── Skills panel ───────────────────────────────────────────────────────────
  const skillsPanel = document.createElement("div");
  skillsPanel.style.cssText = "display:none;";

  const skillsDefault = document.createElement("div");
  skillsDefault.style.cssText = "display:block;padding:16px;";
  const skillRows: HTMLElement[] = [];
  SKILLS_DATA.forEach(({ label, maxFill }) => {
    const row = document.createElement("div");
    row.className = "am-skill-row";
    row.style.cursor = "pointer";
    const lbl = document.createElement("div");
    lbl.className = "am-skill-label";
    lbl.textContent = label;
    row.appendChild(lbl);
    const meter = document.createElement("div");
    meter.className = "skill-meter";
    meter.dataset.maxFill = String(maxFill);
    for (let i = 0; i < 32 * 3; i++) {
      const dot = document.createElement("span");
      dot.className = "sg-dot";
      dot.style.opacity = "0";
      meter.appendChild(dot);
    }
    row.appendChild(meter);
    skillRows.push(row);
    row.addEventListener("mouseenter", () => {
      skillRows.forEach(r => {
        r.style.transition = "opacity 1500ms ease";
        r.style.opacity = r === row ? "1" : "0.28";
      });
      opts?.onItemHover?.(label);
    });
    row.addEventListener("mouseleave", () => {
      skillRows.forEach(r => {
        r.style.transition = "opacity 1500ms ease";
        r.style.opacity = "1";
      });
      opts?.onItemHover?.(null);
    });
    row.addEventListener("click", () => { track("skill_clicked", { skill: label }); showResponse(label, fetchSkillResponse(label), skillsCtx); });
    skillsDefault.appendChild(row);
  });
  skillsPanel.appendChild(skillsDefault);

  const skillsResponse = document.createElement("div");
  skillsResponse.style.cssText = "display:none;flex-direction:column;padding:12px 16px;gap:16px;opacity:0;";
  const skillsRespHeader = document.createElement("div");
  skillsRespHeader.style.cssText = "display:flex;align-items:center;";
  const skillsBackBtn = makeCloseBtn("&#x2190;");
  skillsBackBtn.addEventListener("click", e => { e.stopPropagation(); hideResponse(skillsCtx); });
  skillsRespHeader.appendChild(skillsBackBtn);
  skillsResponse.appendChild(skillsRespHeader);
  const skillsYouSection = document.createElement("div");
  const skillsYouMsg = document.createElement("div");
  skillsYouMsg.style.cssText = [
    "color:rgba(255,255,255,0.85)",
    `background:${COLOR.msgBg}`,
    `border-radius:${RADIUS.card}`,
    "padding:8px 12px;margin-top:4px;display:inline-block",
  ].join(";");
  applyTextStyle(skillsYouMsg, "inputText");
  skillsYouSection.appendChild(makeEyebrow("You"));
  skillsYouSection.appendChild(skillsYouMsg);
  skillsResponse.appendChild(skillsYouSection);
  const skillsJustisSection = document.createElement("div");
  const skillsJustisMsg = document.createElement("div");
  skillsJustisMsg.style.cssText = `color:${COLOR.text};margin-top:4px`;
  applyTextStyle(skillsJustisMsg, "body");
  const skillsInfoDot = makeInfoDot();
  skillsJustisSection.appendChild(makeEyebrow("Justis"));
  skillsJustisSection.appendChild(skillsJustisMsg);
  skillsJustisSection.appendChild(skillsInfoDot.el);
  skillsResponse.appendChild(skillsJustisSection);
  skillsPanel.appendChild(skillsResponse);

  // ── Clients panel ──────────────────────────────────────────────────────────
  const clientsPanel = document.createElement("div");
  clientsPanel.style.cssText = "display:none;";

  const clientsDefault = document.createElement("div");
  clientsDefault.style.cssText = "display:block;padding:16px;";
  const logoGrid = document.createElement("div");
  logoGrid.style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:10px;";
  // Per-logo size overrides — tune values here to achieve optical consistency.
  // Each value replaces the default "max-height:40px;max-width:70%".
  const LOGO_SIZES: Record<string, string> = {
    "CC_logo.png":    "max-height:32px;max-width:58%",   // wide script wordmark
    "audi.png":       "max-height:70px;max-width:52%",   // four rings
    "bk.png":         "max-height:65px;max-width:48%",   // icon — allow taller
    "google.png":     "max-height:65px;max-width:56%",   // G icon
    "intuit.png":     "max-height:85px;max-width:58%",   // thin wordmark
    "microsoft.png":  "max-height:90px;max-width:65px",  // square icon
  };
  const LOGO_FILES: { file: string; company: string }[] = [
    { file: "CC_logo.png",   company: "Coca-Cola" },
    { file: "audi.png",      company: "Audi"                 },
    { file: "bk.png",        company: "Burger King"          },
    { file: "google.png",    company: "Google"               },
    { file: "intuit.png",    company: "Intuit"               },
    { file: "microsoft.png", company: "Microsoft"            },
  ];
  LOGO_FILES.forEach(({ file: filename, company }) => {
    const cell = document.createElement("div");
    cell.style.cssText = [
      `aspect-ratio:1;border-radius:8px;cursor:pointer`,
      `border:1px solid ${COLOR.borderSubtle}`,
      `transition:border-color 700ms ease`,
      "display:flex;align-items:center;justify-content:center",
    ].join(";");
    const img = document.createElement("img");
    img.src = ASSET_BASE + `/images/${filename}`;
    img.alt = company;
    const sizeOverride = LOGO_SIZES[filename] ?? "max-height:40px;max-width:70%";
    img.style.cssText = `${sizeOverride};width:auto;height:auto;display:block;transition:filter 300ms ease;`;
    cell.addEventListener("mouseenter", () => {
      cell.style.borderColor = "rgba(255,255,255,1)";
      img.style.filter       = "brightness(1.3)";
      opts?.onItemHover?.(company);
    });
    cell.addEventListener("mouseleave", () => {
      cell.style.borderColor = COLOR.borderSubtle;
      img.style.filter       = "";
      opts?.onItemHover?.(null);
    });
    cell.addEventListener("click", () => { track("client_clicked", { company }); showResponse(company, fetchLogoResponse(company), clientsCtx); });
    cell.appendChild(img);
    logoGrid.appendChild(cell);
  });
  clientsDefault.appendChild(logoGrid);
  clientsPanel.appendChild(clientsDefault);

  const clientsResponse = document.createElement("div");
  clientsResponse.style.cssText = "display:none;flex-direction:column;padding:12px 16px;gap:16px;opacity:0;";
  const clientsRespHeader = document.createElement("div");
  clientsRespHeader.style.cssText = "display:flex;align-items:center;";
  const clientsBackBtn = makeCloseBtn("&#x2190;");
  clientsBackBtn.addEventListener("click", e => { e.stopPropagation(); hideResponse(clientsCtx); });
  clientsRespHeader.appendChild(clientsBackBtn);
  clientsResponse.appendChild(clientsRespHeader);
  const clientsYouSection = document.createElement("div");
  const clientsYouMsg = document.createElement("div");
  clientsYouMsg.style.cssText = [
    "color:rgba(255,255,255,0.85)",
    `background:${COLOR.msgBg}`,
    `border-radius:${RADIUS.card}`,
    "padding:8px 12px;margin-top:4px;display:inline-block",
  ].join(";");
  applyTextStyle(clientsYouMsg, "inputText");
  clientsYouSection.appendChild(makeEyebrow("You"));
  clientsYouSection.appendChild(clientsYouMsg);
  clientsResponse.appendChild(clientsYouSection);
  const clientsJustisSection = document.createElement("div");
  const clientsJustisMsg = document.createElement("div");
  clientsJustisMsg.style.cssText = `color:${COLOR.text};margin-top:4px`;
  applyTextStyle(clientsJustisMsg, "body");
  const clientsInfoDot = makeInfoDot();
  clientsJustisSection.appendChild(makeEyebrow("Justis"));
  clientsJustisSection.appendChild(clientsJustisMsg);
  clientsJustisSection.appendChild(clientsInfoDot.el);
  clientsResponse.appendChild(clientsJustisSection);
  clientsPanel.appendChild(clientsResponse);

  body.appendChild(overviewPanel);
  body.appendChild(skillsPanel);
  body.appendChild(clientsPanel);

  // ── Response contexts ─────────────────────────────────────────────────────
  overviewCtx = {
    defaultEl:      overviewDefault,
    defaultDisplay: "flex",
    responseEl:     overviewResponse,
    youMsgEl:       youMsg,
    justisMsgEl:    justisMsg,
    infoDot:        overviewInfoDot,
    onHide:         () => triggerBioAnim(),
  };
  skillsCtx = {
    defaultEl:      skillsDefault,
    defaultDisplay: "block",
    responseEl:     skillsResponse,
    youMsgEl:       skillsYouMsg,
    justisMsgEl:    skillsJustisMsg,
    infoDot:        skillsInfoDot,
    onHide:         () => {},
  };
  clientsCtx = {
    defaultEl:      clientsDefault,
    defaultDisplay: "block",
    responseEl:     clientsResponse,
    youMsgEl:       clientsYouMsg,
    justisMsgEl:    clientsJustisMsg,
    infoDot:        clientsInfoDot,
    onHide:         () => {},
  };

  // ── State ──────────────────────────────────────────────────────────────────
  let isOpen              = false;
  let isAnimating         = false;
  let isTabAnimating      = false;
  let isResponseAnimating = false;
  let pillActualW         = PILL_W;
  let bioAnimPlayed       = false;
  let coverFadeTimer: ReturnType<typeof setTimeout> | null = null;

  // ── Response helpers ───────────────────────────────────────────────────────
  async function fetchSkillResponse(label: string): Promise<string> {
    return fetchReply(`Tell me about your ${label} skills.`);
  }
  async function fetchLogoResponse(company: string): Promise<string> {
    return fetchReply(`Tell me about your work with ${company}.`);
  }

  async function showResponse(message: string, response: string | Promise<string>, ctx: ResponseCtx) {
    if (isResponseAnimating) return;
    isResponseAnimating = true;

    ctx._savedHeight = container.offsetHeight;
    ctx.youMsgEl.textContent = message;

    // Fade out tab default + collapse modal to just the bottom row
    ctx.defaultEl.style.transition = "opacity 150ms ease";
    ctx.defaultEl.style.opacity    = "0";
    bottomRow.style.transition    = `padding-bottom 280ms ${EASE}`;
    bottomRow.style.paddingBottom = "0";
    container.style.transition    = `height 280ms ${EASE}`;
    container.style.height        = `${BOTTOM_ROW_H}px`;
    await wait(290);
    ctx.defaultEl.style.display = "none";

    // Immediately swap to retrieving state, then fade to pulsing dim
    // (placeholder changes before any opacity animation so "Ask me anything" is never visible)
    chatInput.value       = "";
    chatInput.placeholder = "Retrieving\u2026";
    chatInput.disabled    = true;
    submitBtn.disabled    = true;
    submitBtn.style.transition = "opacity 180ms ease";
    submitBtn.style.opacity    = "0";
    dotIconCtrl.startRetrieve();
    chatInput.style.transition = "opacity 180ms ease";
    chatInput.style.opacity    = "0.25";
    await wait(190);
    // Clear CSS transition so the RAF pulse has immediate per-frame control
    chatInput.style.transition = "none";

    // Brightness pulse synced to ring rotations.
    const PULSE_CYCLE = 900;
    let pulseCancelled = false;
    let pulseStartTime: number | null = null;
    const pulseTick = (now: number) => {
      if (pulseCancelled) return;
      if (pulseStartTime === null) pulseStartTime = now;
      const cycleT = (now - pulseStartTime) % PULSE_CYCLE / PULSE_CYCLE;
      const easedT = cycleT < 0.5
        ? 2 * cycleT * cycleT
        : 1 - Math.pow(-2 * cycleT + 2, 2) / 2;
      chatInput.style.opacity = (0.25 + 0.60 * Math.sin(easedT * Math.PI)).toFixed(3);
      requestAnimationFrame(pulseTick);
    };
    requestAnimationFrame(pulseTick);

    // Await both: retrieve animation AND response resolution (whichever is longer)
    const [, responseText] = await Promise.all([
      wait(RETRIEVE_DURATION_MS),
      typeof response === "string" ? Promise.resolve(response) : response,
    ]);

    // Stop pulse + icon — restore transition for subsequent opacity changes
    pulseCancelled = true;
    dotIconCtrl.stopRetrieve();
    chatInput.style.transition = "opacity 150ms ease";

    // Build spans now (before height measurement) so scrollHeight is accurate
    buildResponseSpans(responseText, ctx.justisMsgEl);

    // Measure response content height
    ctx.responseEl.style.visibility = "hidden";
    ctx.responseEl.style.display    = "flex";
    const responseH = ctx.responseEl.scrollHeight;
    ctx.responseEl.style.display    = "none";
    ctx.responseEl.style.visibility = "";

    const targetH = Math.min(
      header.offsetHeight + BOTTOM_ROW_H + responseH + 24,
      modalHeight()
    );

    // Expand to response height
    bottomRow.style.transition    = `padding-bottom 320ms ${EASE}`;
    bottomRow.style.paddingBottom = "4px";
    container.style.transition    = `height 320ms ${EASE}`;
    container.style.height        = `${targetH}px`;

    ctx.responseEl.style.display    = "flex";
    ctx.responseEl.style.opacity    = "0";
    ctx.responseEl.style.transition = "opacity 200ms ease";
    await wait(160);
    ctx.responseEl.style.opacity = "1";
    await wait(210);

    animateResponseSpans(ctx.justisMsgEl);
    if (response instanceof Promise) setTimeout(() => ctx.infoDot.show(), 600);

    // Restore input + submit arrow
    chatInput.placeholder      = "Ask me anything\u2026";
    chatInput.disabled         = false;
    chatInput.style.transition = "opacity 150ms ease";
    chatInput.style.opacity    = "1";
    submitBtn.disabled         = false;
    submitBtn.style.transition = "opacity 150ms ease";
    submitBtn.style.opacity    = "1";
    isResponseAnimating        = false;
  }

  async function hideResponse(ctx: ResponseCtx) {
    if (isResponseAnimating) return;
    isResponseAnimating = true;

    ctx.infoDot.hide();
    dotIconCtrl.rotateInner(-60, 300); // inner ring rotates left — going back
    ctx.responseEl.style.transition = "opacity 150ms ease";
    ctx.responseEl.style.opacity    = "0";
    container.style.transition      = `height 300ms ${EASE}`;
    container.style.height          = `${ctx._savedHeight ?? modalHeight()}px`;
    await wait(160);
    ctx.responseEl.style.display = "none";

    ctx.defaultEl.style.display    = ctx.defaultDisplay;
    ctx.defaultEl.style.opacity    = "0";
    ctx.defaultEl.style.transition = "opacity 180ms ease";
    await wait(20);
    ctx.defaultEl.style.opacity = "1";
    await wait(190);

    ctx.onHide();
    isResponseAnimating = false;
  }

  backBtn.addEventListener("click", e => { e.stopPropagation(); hideResponse(overviewCtx); });

  // ── Chat input ─────────────────────────────────────────────────────────────
  function activeCtx(): ResponseCtx {
    if (activeTab === "skills")  return skillsCtx;
    if (activeTab === "clients") return clientsCtx;
    return overviewCtx;
  }

  chatInput.addEventListener("keydown", e => {
    if (e.key !== "Enter") return;
    const msg = chatInput.value.trim();
    if (!msg) return;
    chatInput.value = "";
    setSubmitHighlighted(false);
    track("question_submitted", { question: msg });
    showResponse(msg, fetchReply(msg), activeCtx());
  });
  chatInput.addEventListener("input", () => setSubmitHighlighted(!!chatInput.value.trim()));
  chatInput.addEventListener("click", e => e.stopPropagation());

  // ── Tab switching ──────────────────────────────────────────────────────────
  async function switchTab(id: TabId) {
    if (id === activeTab || isTabAnimating || isAnimating || isResponseAnimating) return;
    isTabAnimating = true;

    // Collapse to bottom row
    modalContent.style.transition    = "opacity 150ms ease";
    modalContent.style.opacity       = "0";
    modalContent.style.pointerEvents = "none";
    bottomRow.style.transition    = `padding-bottom 280ms ${EASE}`;
    bottomRow.style.paddingBottom = "0";   // remove asymmetric padding during collapsed state
    container.style.transition    = `height 280ms ${EASE}`;
    container.style.height        = `${BOTTOM_ROW_H}px`;
    await wait(290);

    // Swap tab content
    activeTab = id;
    overviewPanel.style.display = id === "overview" ? "flex" : "none";
    skillsPanel.style.display   = id === "skills"   ? "block" : "none";
    clientsPanel.style.display  = id === "clients"  ? "block" : "none";
    tabEls.forEach((btn, tid) => {
      btn.style.color      = tabColor(tid);
      btn.style.fontWeight = tabWeight(tid);
    });

    // Compute expand height — skills and clients tabs shrink to fit content
    let expandH = modalHeight();
    if (id === "skills") {
      const headerH = header.offsetHeight + 1; // +1 for divider
      expandH = Math.min(headerH + skillsPanel.scrollHeight + BOTTOM_ROW_H, modalHeight());
    } else if (id === "clients") {
      const headerH = header.offsetHeight + 1;
      expandH = Math.min(headerH + clientsPanel.scrollHeight + BOTTOM_ROW_H, modalHeight());
    }

    // Expand to target height, fade content in partway through (restore bottom padding)
    bottomRow.style.transition    = `padding-bottom 320ms ${EASE}`;
    bottomRow.style.paddingBottom = "4px";
    container.style.transition    = `height 320ms ${EASE}`;
    container.style.height        = `${expandH}px`;
    await wait(120);
    modalContent.style.transition    = "opacity 220ms ease";
    modalContent.style.opacity       = "1";
    modalContent.style.pointerEvents = "all";
    await wait(220);

    if (id === "overview") triggerBioAnim();
    if (id === "clients")  triggerClientAnim();
    isTabAnimating = false;
  }

  function triggerBioAnim() {
    const chipBtns = Array.from(chipsRow.querySelectorAll<HTMLElement>(".am-chip"));

    if (bioAnimPlayed) {
      // Already seen — snap to visible without re-animating
      if (TEXT_ANIM_MODE === "block-slide") {
        bioWrap.style.transition = "none"; bioWrap.style.opacity = "1"; bioWrap.style.transform = "translateY(0)";
      } else {
        bioTokens.forEach(s => { s.style.transition = "none"; s.style.opacity = "1"; if (hasDrift) s.style.transform = "translateY(0)"; });
      }
      chipBtns.forEach(c => { c.style.transition = "none"; c.style.opacity = "1"; if (hasDrift) c.style.transform = "translateY(0)"; });
      return;
    }
    bioAnimPlayed = true;

    if (TEXT_ANIM_MODE === "block-slide") {
      bioWrap.style.transition = "none";
      bioWrap.style.opacity    = "0";
      bioWrap.style.transform  = "translateY(8px)";
      chipBtns.forEach(c => { c.style.transition = "none"; c.style.opacity = "0"; c.style.transform = "translateY(6px)"; });
      requestAnimationFrame(() => requestAnimationFrame(() => {
        bioWrap.style.transition = "opacity 500ms ease, transform 500ms ease";
        bioWrap.style.opacity    = "1";
        bioWrap.style.transform  = "translateY(0)";
        chipBtns.forEach((c, i) => {
          const delay = 400 + i * 80;
          c.style.transition = `opacity 350ms ease ${delay}ms, transform 350ms ease ${delay}ms`;
          c.style.opacity    = "1";
          c.style.transform  = "translateY(0)";
        });
      }));
      return;
    }

    const itemDelay    = TEXT_ANIM_MODE === "char-drift" ? 12 : TEXT_ANIM_MODE === "word-fade" ? 28 : 22;
    const itemDuration = TEXT_ANIM_MODE === "char-drift" ? 300 : TEXT_ANIM_MODE === "word-drift" ? 400 : 350;

    bioTokens.forEach(s => {
      s.style.transition = "none";
      s.style.opacity    = "0";
      if (hasDrift) s.style.transform = "translateY(6px)";
    });
    chipBtns.forEach(c => {
      c.style.transition = "none";
      c.style.opacity    = "0";
      if (hasDrift) c.style.transform = "translateY(6px)";
    });

    requestAnimationFrame(() => requestAnimationFrame(() => {
      bioTokens.forEach((s, i) => {
        const delay = i * itemDelay;
        s.style.transition = `opacity ${itemDuration}ms ease ${delay}ms${hasDrift ? `, transform ${itemDuration}ms ease ${delay}ms` : ""}`;
        s.style.opacity    = "1";
        if (hasDrift) s.style.transform = "translateY(0)";
      });

      // Chips follow in same wave — start at 80% of bio token stagger duration
      const chipStart    = Math.round(bioTokens.length * itemDelay * 0.8);
      const chipInterval = TEXT_ANIM_MODE === "char-drift" ? 60 : 80;
      chipBtns.forEach((c, i) => {
        const delay = chipStart + i * chipInterval;
        c.style.transition = `opacity 350ms ease ${delay}ms${hasDrift ? `, transform 350ms ease ${delay}ms` : ""}`;
        c.style.opacity    = "1";
        if (hasDrift) c.style.transform = "translateY(0)";
      });
    }));
  }

  // Pre-populate target with invisible spans so scrollHeight is accurate at measurement time.
  function buildResponseSpans(text: string, target: HTMLElement) {
    target.innerHTML = "";
    const paragraphs = text.split(/\n\n+/).map(p => p.replace(/\n/g, " ").trim()).filter(Boolean);
    paragraphs.forEach((para, pi) => {
      const block = document.createElement("div");
      block.style.marginBottom = pi < paragraphs.length - 1 ? "12px" : "";
      para.split(" ").forEach(word => {
        const span = document.createElement("span");
        span.textContent = word;
        span.style.cssText = `display:inline-block;color:${COLOR.text};opacity:0;transform:translateY(6px)`;
        applyTextStyle(span, "body");
        block.appendChild(span);
        block.appendChild(document.createTextNode(" "));
      });
      target.appendChild(block);
    });
  }

  // Trigger stagger animation on pre-built spans (call after panel is visible).
  function animateResponseSpans(target: HTMLElement) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      Array.from(target.querySelectorAll<HTMLElement>("span")).forEach((s, i) => {
        s.style.transition = `opacity 400ms ease ${i * 22}ms, transform 400ms ease ${i * 22}ms`;
        s.style.opacity    = "1";
        s.style.transform  = "translateY(0)";
      });
    }));
  }

  function triggerClientAnim() {
    Array.from(logoGrid.children).forEach((c, i) => {
      const el = c as HTMLElement;
      el.style.transition = "none"; el.style.opacity = "0";
      setTimeout(() => {
        el.style.transition = "opacity 0.3s ease, border-color 700ms ease";
        el.style.opacity = "1";
      }, i * 55 + 30);
    });
  }


  // ── Open / close ───────────────────────────────────────────────────────────
  async function openModal() {
    if (isOpen || isAnimating) return;
    isAnimating = true;
    track("modal_opened");
    container.style.zIndex = "9990";
    // Stop Lenis smooth scroll so wheel events reach our listener correctly.
    // Do NOT set overflow on body/html/.body-wrap — it breaks position:sticky children.
    // Background scroll is blocked by the _preventScroll listeners below.
    (window as any).lenis?.stop?.();
    opts?.onOpen?.();

    // Own all wheel/touch events while modal is open — Lenis may still preventDefault
    // even after .stop(), so we can't rely on the browser's default scroll action
    // for .am-body. Instead: always preventDefault, but manually apply deltaY / touch
    // delta to .am-body when the event originates inside it.
    _onTouchStart = (e: TouchEvent) => { _lastTouchY = e.touches[0]?.clientY ?? 0; };
    document.addEventListener("touchstart", _onTouchStart as EventListener, { passive: true });
    _preventScroll = (e: Event) => {
      e.preventDefault();
      const amBodyEl = (e.target as HTMLElement)?.closest?.(".am-body") as HTMLElement | null;
      if (!amBodyEl) return;
      if (e instanceof TouchEvent) {
        const touchY = e.touches[0]?.clientY ?? _lastTouchY;
        const delta = _lastTouchY - touchY;
        _lastTouchY = touchY;
        amBodyEl.scrollTop += delta;
      } else {
        amBodyEl.scrollTop += (e as WheelEvent).deltaY;
      }
    };
    document.addEventListener("wheel", _preventScroll, { passive: false });
    document.addEventListener("touchmove", _preventScroll, { passive: false });

    // Hide hero text element (Webflow ID) while modal is open
    const heroText = document.getElementById("hero-text");
    if (heroText) {
      heroText.style.transition = "opacity 200ms ease";
      heroText.style.opacity = "0";
    }


    // Snap hover scale instantly before animation begins
    container.style.transition = "none";
    bottomRow.style.transition = "none";
    container.style.transform  = "scale(1)";
    bottomRow.style.transform  = "scale(1)";
    await wait(16); // one frame for paint

    pillActualW = container.getBoundingClientRect().width;

    // Fade out pill label — icon stays visible
    pillLabel.style.transition = "opacity 160ms ease";
    pillLabel.style.opacity    = "0";
    await wait(170);

    // Collapse to circle
    container.style.transition = "width 200ms ease-in, border-radius 200ms ease-in";
    container.style.width      = `${PILL_H}px`;
    await wait(210);

    // Width expands + outer ring rotates CW simultaneously (outer ring maps horizontal motion)
    // On mobile: pill is centred; simultaneously slide left to left-anchor as width grows.
    container.style.transition = `width 300ms ${EASE}, left 300ms ${EASE}`;
    container.style.width      = `${modalWidth()}px`;
    container.style.left       = `${localLeft()}px`;
    dotIconCtrl.rotateOuter(60, 350);
    await wait(350); // wait for both width and outer ring to complete

    // Remove pill label from layout before chatInput fades in (already none when fromIcon)
    pillLabel.style.display = "none";

    // Height expands + icon rises + inner ring rotates — all simultaneously
    // Border-top, paddingBottom, and bottomRow height all fire here so the icon's
    // upward movement is synchronised with the inner ring rotation
    bottomRow.style.borderTop     = `1px solid ${COLOR.borderSubtle}`;
    bottomRow.style.transition    = `transform 300ms ease, height 360ms ${EASE}, padding-bottom 360ms ${EASE}`;
    bottomRow.style.paddingBottom = "4px";
    bottomRow.style.height        = `${BOTTOM_ROW_H}px`;
    container.style.transition    = `height 360ms ${EASE}, border-radius 360ms ${EASE}`;
    container.style.height        = `${modalHeight()}px`;
    container.style.borderRadius  = RADIUS.modal;
    dotIconCtrl.rotateInner(-60, 350);

    // Content fades in partway through height expansion
    await wait(120);
    modalContent.style.transition    = "opacity 220ms ease";
    modalContent.style.opacity       = "1";
    modalContent.style.pointerEvents = "all";

    // Fade in chat input + submit arrow at same time (already correctly positioned)
    chatInput.style.transition    = "opacity 220ms ease";
    chatInput.style.opacity       = "1";
    chatInput.style.pointerEvents = "all";
    submitBtn.style.transition    = "opacity 220ms ease";
    submitBtn.style.opacity       = "1";
    submitBtn.style.pointerEvents = "all";

    await wait(260);

    container.style.cursor = "default";
    triggerBioAnim();
    photoVideo.play().catch(() => {});
    if (coverFadeTimer) clearTimeout(coverFadeTimer);
    coverFadeTimer = setTimeout(() => {
      photoCover.style.transition = "opacity 700ms ease";
      requestAnimationFrame(() => { photoCover.style.opacity = "0"; });
    }, 1000);
    isAnimating = false;
    isOpen      = true;
  }

  async function closeModal() {
    if (!isOpen || isAnimating || isTabAnimating) return;
    isAnimating = true;
    track("modal_closed");
    container.style.zIndex = "9985";

    photoVideo.pause();
    photoVideo.currentTime = 0;
    if (coverFadeTimer) { clearTimeout(coverFadeTimer); coverFadeTimer = null; }
    photoCover.style.transition = "none";
    photoCover.style.opacity    = "1";

    // Fade out modal content, chat input, and submit arrow
    modalContent.style.transition    = "opacity 180ms ease";
    modalContent.style.opacity       = "0";
    modalContent.style.pointerEvents = "none";
    chatInput.style.transition       = "opacity 180ms ease";
    chatInput.style.opacity          = "0";
    chatInput.style.pointerEvents    = "none";
    submitBtn.style.transition       = "opacity 180ms ease";
    submitBtn.style.opacity          = "0";
    submitBtn.style.pointerEvents    = "none";
    await wait(190);

    // Restore pill label to layout (invisible) so width calc is stable
    pillLabel.style.display    = "";
    pillLabel.style.transition = "none";
    pillLabel.style.opacity    = "0";

    // Hide border-top during collapse; shrink bottom row back to pill height
    bottomRow.style.borderTop     = "0px solid transparent";
    bottomRow.style.transition    = `transform 300ms ease, height 300ms ${EASE}, padding-bottom 300ms ${EASE}`;
    bottomRow.style.paddingBottom = "0";
    bottomRow.style.height        = `${PILL_H}px`;

    // Collapse height — Part 1: inner ring CW (undo CCW from open)
    container.style.transition   = `height 300ms ${EASE}, border-radius 300ms ease`;
    container.style.height       = `${PILL_H}px`;
    container.style.borderRadius = `${PILL_H / 2}px`;
    dotIconCtrl.rotateInner(60, 280);
    await wait(310);

    // Collapse width to circle — Part 2: outer ring CCW (undo CW from open)
    dotIconCtrl.rotateOuter(-60, 250);
    container.style.transition = `width 250ms ${EASE}`;
    container.style.width      = `${PILL_H}px`;
    await wait(260);

    // Expand to pill width — simultaneously slide left back to pill's home position
    container.style.transition = "width 220ms ease-out, left 220ms ease-out";
    container.style.width      = `${pillActualW}px`;
    container.style.left       = `${pillLeft()}px`;
    container.style.cursor     = "pointer";
    await wait(220);

    // Pill is now at correct size — fade in label
    pillLabel.style.transition = "opacity 180ms ease";
    pillLabel.style.opacity    = "1";
    await wait(190);

    // Reset all state
    activeTab = "overview";
    overviewPanel.style.display = "flex";
    skillsPanel.style.display   = "none";
    clientsPanel.style.display  = "none";
    tabEls.forEach((btn, tid) => {
      btn.style.color      = tabColor(tid);
      btn.style.fontWeight = tabWeight(tid);
    });

    overviewResponse.style.display   = "none";
    overviewResponse.style.opacity   = "0";
    overviewDefault.style.display    = "flex";
    overviewDefault.style.opacity    = "1";
    overviewDefault.style.transition = "none";
    skillsResponse.style.display  = "none";
    skillsResponse.style.opacity  = "0";
    skillsDefault.style.display   = "block";
    skillsDefault.style.opacity   = "1";
    clientsResponse.style.display = "none";
    clientsResponse.style.opacity = "0";
    clientsDefault.style.display  = "block";
    clientsDefault.style.opacity  = "1";
    if (TEXT_ANIM_MODE === "block-slide") {
      bioWrap.style.transition = "none";
      bioWrap.style.opacity    = "0";
      bioWrap.style.transform  = "translateY(8px)";
    } else {
      bioTokens.forEach(s => { s.style.transition = "none"; s.style.opacity = "0"; if (hasDrift) s.style.transform = "translateY(6px)"; });
    }
    chipsRow.querySelectorAll<HTMLElement>(".am-chip").forEach(c => {
      c.style.transition = "none"; c.style.opacity = "0"; if (hasDrift) c.style.transform = "translateY(6px)";
    });

    isTabAnimating      = false;
    isAnimating         = false;
    isResponseAnimating = false;
    isOpen              = false;
    dotIconCtrl.resetRotation();
    bioAnimPlayed       = false;

    (window as any).lenis?.start?.();
    opts?.onClose?.();

    // Remove scroll prevention listeners
    if (_onTouchStart) {
      document.removeEventListener("touchstart", _onTouchStart as EventListener);
      _onTouchStart = null;
    }
    if (_preventScroll) {
      document.removeEventListener("wheel", _preventScroll);
      document.removeEventListener("touchmove", _preventScroll);
      _preventScroll = null;
    }

    // Restore hero text
    const heroText = document.getElementById("hero-text");
    if (heroText) heroText.style.opacity = "";

    // Restore transform transition so post-close hover scales smoothly
    container.style.transition = "transform 300ms ease";
    bottomRow.style.transition = "transform 300ms ease";

  }

  // ── Interactions ───────────────────────────────────────────────────────────
  container.addEventListener("click", () => { if (!isOpen && !isAnimating) openModal(); });

  container.addEventListener("mouseenter", () => {
    if (!isOpen && !isAnimating) {
      container.style.transform = `scale(${PILL_HOVER_SCALE})`;
      bottomRow.style.transform = `scale(${1 / PILL_HOVER_SCALE})`;
    }
  });
  container.addEventListener("mouseleave", () => {
    container.style.transform = "scale(1)";
    bottomRow.style.transform = "scale(1)";
  });

  document.addEventListener("keydown", e => {
    if (e.key === "Escape" && isOpen && !isAnimating) closeModal();
  });

  // ── Resize ─────────────────────────────────────────────────────────────────
  window.addEventListener("resize", () => {
    container.style.left = isOpen ? `${localLeft()}px` : `${pillLeft()}px`;
    if (isOpen) {
      container.style.width  = `${modalWidth()}px`;
      container.style.height = `${modalHeight()}px`;
    }
  });

  return { isOpen: () => isOpen };
}
